// Updated Dashboard component with IndexedDB integration

import React, { useState, useEffect } from 'react';
import { PlusOutlined, EditOutlined, DeleteOutlined, SyncOutlined, WifiOutlined } from '@ant-design/icons';
import { useAuth } from '@/hooks/useAuth';
import { Button, Card, Col, Form, Input, Modal, Row, Select, Space, Table, message, Badge, Tooltip, Statistic, Typography } from 'antd';
import { Controller, useForm } from 'react-hook-form';
import ShipForm from './ShipForm';
import instance from '@/services/axios.config';
import userService from '@/services/user.service';
import InspectionDB from '@/services/indexDB/inspectionStore';
import InspectionBtn from '@/components/InspectionBtn';
import { useDispatch, useSelector } from 'react-redux';
import toast from "react-hot-toast";

// Vessel type options
const vesselTypes = [
    'Cargo',
    'Tanker',
    'Container',
    'Bulk Carrier',
    'Passenger',
    'Oil Tanker',
    'Chemical Tanker',
    'LNG Carrier',
    'Cruise Ship',
    'Ferry'
];

const engineTypes = [
    'Diesel',
    'Gas Turbine',
    'Steam',
    'Electric',
    'Hybrid'
];

const Dashboard = () => {
    const [ships, setShips] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editingShip, setEditingShip] = useState(null);
    const [selectedShipId, setSelectedShipId] = useState(null);
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [syncing, setSyncing] = useState(false);
    const [db] = useState(() => new InspectionDB());
    const dispatch = useDispatch()
    const { control, handleSubmit, formState: { errors }, reset, setValue } = useForm();
    const { role, user } = useAuth();
    const userId = user?.user_id;
    const { subscription } = useSelector(state => state.auth)


    // Load ships from IndexedDB
    const loadShipsFromIndexedDB = async () => {
        try {
            setLoading(true);
            const localShips = await db.getAllShips(userId);

            setShips(localShips);
        } catch (error) {
            console.error('Error loading ships from IndexedDB:', error);
            toast.error('Failed to load ships from local storage');
        } finally {
            setLoading(false);
        }
    };

    // Add ship to IndexedDB
    const addShip = async (shipData) => {
        try {
            const newShip = await db.addShip({
                ...shipData,
                user_id: userId,
                synced: false
            });

            // Reload ships from IndexedDB
            await loadShipsFromIndexedDB();
            return newShip;
        } catch (error) {
            console.error('Error adding ship:', error);
            throw error;
        }
    };

    // Update ship in IndexedDB
    const updateShip = async (shipId, updateData) => {
        try {
            const updatedShip = await db.updateShip(shipId, {
                ...updateData,
                synced: false // Mark as unsynced after update
            });

            // Reload ships from IndexedDB
            await loadShipsFromIndexedDB();
            return updatedShip;
        } catch (error) {
            console.error('Error updating ship:', error);
            throw error;
        }
    };

    // Delete ship from IndexedDB
    const deleteShip = async (shipId) => {
        try {
            await db.deleteShip(shipId);

            // Reload ships from IndexedDB
            await loadShipsFromIndexedDB();
        } catch (error) {
            console.error('Error deleting ship:', error);
            throw error;
        }
    };

    // Sync ships to cloud when online
    const syncShipsToCloud = async () => {
        if (!isOnline || syncing) return;

        try {
            setSyncing(true);
            const localShips = await db.getAllShips(userId);
            const unsyncedShips = localShips.filter(ship => !ship.synced);

            if (unsyncedShips.length === 0) {
                setSyncing(false);
                return;
            }


            for (const ship of unsyncedShips) {
                try {
                    if (!ship.server_id) {
                        // New ship - create on server
                        const response = await instance.post('/ships/addusership', {
                            ...ship,
                            user_id: userId
                        });

                        // Update local ship with server_id and mark as synced
                        await db.updateShip(ship.id, {
                            server_id: response.data.id,
                            synced: true
                        });
                    } else {
                        // Existing ship - update on server
                        await instance.put(`/ships/${ship.server_id}/`, {
                            ...ship,
                            user_id: userId
                        });

                        // Mark as synced
                        await db.updateShip(ship.id, {
                            synced: true
                        });
                    }
                } catch (error) {
                    console.error('Error syncing ship to cloud:', ship.name, error);
                    // Continue with other ships even if one fails
                }
            }

            // Reload ships after sync
            await loadShipsFromIndexedDB();
            toast.success('Ships synced successfully!');
        } catch (error) {
            console.error('Error during sync:', error);
            toast.error('Sync failed. Will retry when online.');
        } finally {
            setSyncing(false);
        }
    };

    // Load ships from cloud and save to IndexedDB
    const loadShipsFromCloud = async () => {
        if (!isOnline) return;

        try {
            setSyncing(true);
            const response = await userService.getuserShips(userId);
             // Save cloud ships to IndexedDB
            for (const cloudShip of response?.ships) {
                try {
                     // Check if ship already exists in IndexedDB
                    const existingShips = await db.getAllShips(userId);
                    const existingShip = existingShips.find(s =>
                        s.server_id === cloudShip.imo_number || s.imo_number === cloudShip.imo_number
                    );

                    if (!existingShip) {
                        // Add new ship from cloud
                        await db.addShip({
                            ...cloudShip,
                            server_id: cloudShip.imo_number,
                            user_id: userId,
                            synced: true
                        });
                    } else if (!existingShip.synced) {
                        // Update existing unsynced ship with cloud data
                        await db.updateShip(existingShip.id, {
                            ...cloudShip,
                            server_id: cloudShip.imo_number,
                            synced: true
                        });
                    }
                } catch (error) {
                    console.error('Error saving cloud ship to IndexedDB:', error);
                }
            }

            // Reload ships from IndexedDB
            await loadShipsFromIndexedDB();
        } catch (error) {
            console.error('Error loading ships from cloud:', error);
            toast.error('Failed to load ships from server');
        } finally {
            setSyncing(false);
        }
    };

    // Handle online/offline status
    useEffect(() => {
        const handleOnline = () => {
            setIsOnline(true);
            toast.success('Back online! Syncing data...');
        };

        const handleOffline = () => {
            setIsOnline(false);
            message.info('Working offline. Data will sync when online.');
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    // Initial load
    useEffect(() => {
        if (userId) {
            loadShipsFromIndexedDB();
        }
    }, [userId]);

    // Sync when coming online or when ships change
    useEffect(() => {
        if (isOnline && userId && ships.length > 0) {
            // First load from cloud, then sync local changes
            loadShipsFromCloud().then(() => {
                syncShipsToCloud();
            });
        } else if (isOnline && userId) {
            // Just load from cloud if no local ships
            loadShipsFromCloud();
        }
    }, [isOnline, userId]);

    const handleShipSelect = (shipId) => {
        setSelectedShipId(shipId);
        localStorage.setItem('selectedShipId', shipId.toString());
    };

    const handleAddShip = () => {
        reset(); // Clear the form
        setIsEditMode(false);
        setEditingShip(null);
        setIsModalVisible(true);
    };

    // Open modal for editing existing ship with proper data patching
    const handleEditShip = (shipData) => {
        setEditingShip(shipData);
        setIsEditMode(true);

        // Reset form first
        reset();

        setTimeout(() => {
            // Patch all form fields with ship data
            const formFields = [
                'imo_number', 'name', 'vessel_type', 'deadweight',
                'gross_tonnage', 'flag', 'year_of_build',
                'engine_type', 'engine_model', 'engine_power'
            ];

            formFields.forEach(field => {
                if (shipData[field] !== undefined && shipData[field] !== null) {
                    setValue(field, shipData[field]);
                }
            });
        }, 0);

        setIsModalVisible(true);
    };

    // Handle modal cancel
    const handleCancel = () => {
        setIsModalVisible(false);
        setEditingShip(null);
        reset(); // Clear form data
    };

    // Handle form submission
    const onSubmit = async (data) => {
        try {
            if (isEditMode && editingShip) {
                await updateShip(editingShip.id, data);
                toast.success('Ship updated successfully!');
            } else {
                await addShip(data);
                toast.success('Ship added successfully!');
            }

            setIsModalVisible(false);
            setEditingShip(null);
            reset();

            // Trigger sync if online
            if (isOnline) {
                setTimeout(() => syncShipsToCloud(), 1000);
            }

        } catch (error) {
            console.error('Error submitting form:', error);
            toast.error('An error occurred. Please try again.');
        }
    };

    // Handle delete ship
    const handleDeleteShip = (shipId) => {
        Modal.confirm({
            title: 'Are you sure you want to delete this ship?',
            content: 'This action cannot be undone.',
            okText: 'Yes, Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            async onOk() {
                try {
                    await deleteShip(shipId);

                    // Update selected ship if the deleted ship was selected
                    if (selectedShipId === shipId && ships.length > 1) {
                        const remainingShips = ships.filter(ship => ship.id !== shipId);
                        if (remainingShips.length > 0) {
                            setSelectedShipId(remainingShips[0].id);
                            localStorage.setItem('selectedShipId', remainingShips[0].id.toString());
                        }
                    }

                    toast.success('Ship deleted successfully!');

                    // Trigger sync if online
                    if (isOnline) {
                        setTimeout(() => syncShipsToCloud(), 1000);
                    }
                } catch (error) {
                    toast.error('Failed to delete ship');
                }
            }
        });
    };

    // Manual sync button
    const handleManualSync = async () => {
        if (!isOnline) {
            toast.error('Cannot sync while offline');
            return;
        }

        await loadShipsFromCloud();
        await syncShipsToCloud();
    };

    // Get sync status
    const getSyncStatus = () => {
        const unsyncedShips = ships.filter(ship => !ship.synced);
        return {
            isOnline,
            unsynced: unsyncedShips.length,
            syncing
        };
    };

    // Render sync status indicator
    const renderSyncStatus = () => {
        const { isOnline, unsynced, syncing: isSyncing } = getSyncStatus();

        if (!isOnline) {
            return (
                <Tooltip title={`Offline mode${unsynced > 0 ? ` - ${unsynced} unsynced changes` : ''}`}>
                    <Badge count={unsynced} size="small">
                        <span className="text-red-500 text-lg">📱</span>
                    </Badge>
                </Tooltip>
            );
        }

        if (isSyncing) {
            return (
                <Tooltip title="Syncing data...">
                    <SyncOutlined spin className="text-blue-500 text-lg" />
                </Tooltip>
            );
        }

        if (unsynced > 0) {
            return (
                <Tooltip title={`${unsynced} changes pending sync`}>
                    <Badge count={unsynced} size="small">
                        <Button
                            size="small"
                            type="link"
                            icon={<SyncOutlined />}
                            onClick={handleManualSync}
                            className="text-orange-500"
                        >
                            Sync
                        </Button>
                    </Badge>
                </Tooltip>
            );
        }

        return (
            <Tooltip title="All data synced">
                <WifiOutlined className="text-green-500 text-lg" />
            </Tooltip>
        );
    };

    // Enhanced columns with sync status
    const columns = [
        {
            title: 'Ship Name',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <div className="flex items-center gap-2">
                    <div>
                        <div className="font-medium">{text}</div>
                        <div className="text-sm text-gray-500">{record.deadweight} DWT</div>
                    </div>
                    {!record.synced && (
                        <Badge
                            status="warning"
                            title="Pending sync"
                            className="ml-2"
                        />
                    )}
                </div>
            ),
        },
        {
            title: 'IMO Number',
            dataIndex: 'imo_number',
            key: 'imo_number',
        },
        {
            title: 'Type',
            dataIndex: 'vessel_type',
            key: 'vessel_type',
        },
        {
            title: 'Flag',
            dataIndex: 'flag',
            key: 'flag',
        },
        {
            title: 'Year Built',
            dataIndex: 'year_of_build',
            key: 'year_of_build',
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space size="middle">
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => handleEditShip(record)}
                        className="text-blue-600 hover:text-blue-900"
                        size="small"
                    />
                    <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteShip(record.id)}
                        className="text-red-600 hover:text-red-900"
                        size="small"
                    />
                </Space>
            ),
        },
    ];

    return (
        <div className="min-h-screen bg-gray-50 p-3">
            <div className="max-w-screen-3xl mx-auto sm:px-6 lg:px-8">
                <Typography.Title level={5}>Dashboard</Typography.Title>
                <Row gutter={[24, 24]} className="mb-6 mt-3">
                    <Col xs={8} sm={8} lg={4}>
                        <Card
                            className="text-center shadow-sm hover:shadow-md transition-shadow p-2"
                            bodyStyle={{ padding: '8px' }}
                        >
                            <Statistic
                                title={<span style={{ fontSize: '0.75rem', color: '#666' }}>Remaining Ships</span>}
                                value={subscription?.max_ships}
                                valueStyle={{ color: '#722ed1', fontSize: '1.2rem', fontWeight: 600 }}
                            />

                        </Card>
                    </Col>
                    <Col xs={8} sm={8} lg={4}>
                        <Card
                            className="text-center shadow-sm hover:shadow-md transition-shadow p-2"
                            bodyStyle={{ padding: '8px' }}
                        >
                            <Statistic
                                title={<span style={{ fontSize: '0.75rem', color: '#666' }}>Remaining Users</span>}
                                value={subscription?.max_users}
                                valueStyle={{ color: '#722ed1', fontSize: '1.2rem', fontWeight: 600 }}
                            />

                        </Card>
                    </Col>
                    <Col xs={8} sm={8} lg={4}>
                        <Card
                            className="text-center shadow-sm hover:shadow-md transition-shadow p-2"
                            bodyStyle={{ padding: '8px' }}
                        >
                            <Statistic
                                title={<span style={{ fontSize: '0.75rem', color: '#666' }}>Remaining Reports</span>}
                                value={subscription?.no_of_reports}
                                valueStyle={{ color: '#722ed1', fontSize: '1.2rem', fontWeight: 600 }}
                            />

                        </Card>
                    </Col>
                </Row>
                <div className="bg-gray-300 rounded-xl px-3 py-2 flex justify-between items-center mb-3">
                    <div className="flex items-center gap-3">
                        <h6 className="text-base font-bold text-gray-600">
                            Vessels ({ships?.length})
                        </h6>
                        {renderSyncStatus()}
                    </div>
                    <InspectionBtn
                        type='primary'
                        size='small'
                        style={{ borderRadius: "10px" }}
                        onClick={handleAddShip}
                        icon={<PlusOutlined />}
                    >

                        Add Ship
                    </InspectionBtn>
                </div>

                <div className="bg-white p-2 rounded-lg shadow-md border border-gray-200 overflow-hidden">
                    <Table
                        size='small'
                        className="spotflash-table"

                        columns={columns}
                        dataSource={ships}
                        rowKey="id"
                        pagination={{ pageSize: 10 }}
                        loading={loading}
                    />
                </div>
            </div>

            {/* Single Ship Form Modal */}
            <ShipForm
                isVisible={isModalVisible}
                isEditMode={isEditMode}
                onCancel={handleCancel}
                onSubmit={onSubmit}
                control={control}
                errors={errors}
                handleSubmit={handleSubmit}
                vesselTypes={vesselTypes}
                engineTypes={engineTypes}
            />
        </div>
    );
};

export default Dashboard;